// Standalone chart component code for embedding
// Uses only React.createElement syntax for exportable HTML

export const LINE_CHART_TEMPLATE = `
  // === Shared: FilterControls (standalone) ===
  function StandaloneFilterControls({ data, activeFilter, hoveredFilter, onFilterClick, onFilterHover }) {
    const POSITION_ORDER = ['mayor', 'city council'];
    const structure = { positions: {} };
    (data.lines || []).forEach(l => {
      const pos = l.position || 'Other';
      if (!structure.positions[pos]) structure.positions[pos] = { subs: new Set() };
      if (l.subregion_value) structure.positions[pos].subs.add(l.subregion_value);
    });
    const sortedPositions = Object.keys(structure.positions).sort((a,b) => {
      const ai = POSITION_ORDER.indexOf(a.toLowerCase());
      const bi = POSITION_ORDER.indexOf(b.toLowerCase());
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return a.localeCompare(b);
    });
    const opts = [{ id:'all', label:'All', type:'all' }];
    sortedPositions.forEach(p => {
      const pos = structure.positions[p];
      opts.push({ id:p, label:p, type:'position' });
      Array.from(pos.subs).sort().forEach(s => {
        opts.push({ id:\`\${p}:\${s}\`, label:s, type:'subregion', parentPosition:p });
      });
    });

    return React.createElement(
      'div',
      { className:'flex flex-wrap gap-2 items-baseline' },
      opts.map((f, idx) => {
        const isActive = activeFilter === f.id;
        const isHovered = hoveredFilter === f.id;
        const pieces = [];

        const showOpen = f.type === 'subregion' && idx > 0 && opts[idx-1].type === 'position';
        if (showOpen) pieces.push(React.createElement('span', { key:\`o-\${f.id}\`, className:'text-gray-400 dark:text-gray-600 mx-1' }, '('));

        pieces.push(
          React.createElement(
            'button',
            {
              key:f.id,
              onClick: () => onFilterClick && onFilterClick(f.id),
              onMouseEnter: () => onFilterHover && onFilterHover(f.id),
              onMouseLeave: () => onFilterHover && onFilterHover(null),
              className: \`px-2 py-1 rounded transition-all duration-200 cursor-pointer \${isActive ? 'font-bold text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'} \${!isActive && !isHovered ? 'opacity-70' : 'opacity-100'}\`
            },
            f.label
          )
        );

        const next = opts[idx+1];
        const sameParentNext = next && next.parentPosition === f.parentPosition;
        if (f.type === 'subregion' && next && sameParentNext) {
          pieces.push(React.createElement('span', { key:\`c-\${f.id}\`, className:'text-gray-400 dark:text-gray-600' }, ','));
        }
        if (f.type === 'subregion' && next && !sameParentNext) {
          pieces.push(React.createElement('span', { key:\`cl-\${f.id}\`, className:'text-gray-400 dark:text-gray-600 mx-1' }, ')'));
        }
        if (f.type === 'subregion' && !next) {
          pieces.push(React.createElement('span', { key:\`clx-\${f.id}\`, className:'text-gray-400 dark:text-gray-600 mx-1' }, ')'));
        }

        return React.createElement(React.Fragment, { key:\`frag-\${f.id}\` }, pieces);
      })
    );
  }

  // === Line helpers ===
  function matchesLineFilter(line, filterId) {
    if (filterId === 'all') return true;
    if (line.position === filterId) return true;
    if (filterId && filterId.indexOf(':') !== -1) {
      const parts = filterId.split(':');
      return line.position === parts[0] && String(line.subregion_value) === String(parts[1]);
    }
    return false;
  }
  function formatYAxis(value) {
    if (value >= 1000000) return '$' + (value/1000000).toFixed(1) + 'M';
    if (value >= 1000) return '$' + (value/1000).toFixed(0) + 'K';
    return '$' + value;
  }
  function formatXAxis(dateStr) {
    const d = new Date(dateStr);
    const day = d.getDate();
    if (day <= 7) return d.toLocaleString('en-US', { month:'short' });
    return '';
  }

  // === Line chart component (standalone) ===
  function CampaignLineChart({ data, title, yAxisLabel, xAxisLabel, activeFilter, showLocalFilters }) {
    const { useState } = React;
    const [hoveredLine, setHoveredLine] = useState(null);
    const [hoveredDot, setHoveredDot] = useState(null);
    const [localHoveredFilter, setLocalHoveredFilter] = useState(null);
    const [localActiveFilter, setLocalActiveFilter] = useState(activeFilter || 'all');

    const currentActiveFilter = showLocalFilters ? localActiveFilter : activeFilter;
    const filteredLines = (currentActiveFilter === 'all' ? (data.lines || []) : (data.lines || []).filter(l => matchesLineFilter(l, currentActiveFilter)));
    const safeLines = filteredLines.length ? filteredLines : (data.lines || []);

    // Build recharts data
    const chartData = (safeLines[0] && safeLines[0].points ? safeLines[0].points : []).map((p, idx) => {
      const row = { date: p.date };
      safeLines.forEach(l => {
        const val = (l.points && l.points[idx]) ? l.points[idx].value : null;
        row[l.dataKey] = val;
      });
      return row;
    });

    // Tooltip
    function CustomTooltip({ active, payload, label }) {
      if (!(active && payload && payload.length)) return null;
      const date = new Date(label);
      if (hoveredDot) {
        const dotData = payload.find(p => p.dataKey === hoveredDot);
        if (!dotData) return null;
        return React.createElement(
          'div',
          { className:'tooltip-container-single bg-gray-900 text-white px-3 py-2 rounded shadow-lg' },
          React.createElement('p', { className:'tooltip-value text-xs font-semibold' },
            Number(dotData.value).toLocaleString('en-US', { style:'currency', currency:'USD', minimumFractionDigits:0, maximumFractionDigits:0 })
          ),
          React.createElement('p', { className:'tooltip-date-single text-xs text-gray-300' },
            date.toLocaleDateString('en-US', { month:'short', day:'numeric' })
          )
        );
      }
      return React.createElement(
        'div',
        { className:'tooltip-container bg-gray-900 text-white px-4 py-3 rounded shadow-lg max-w-xs' },
        React.createElement('p', { className:'tooltip-date text-xs text-gray-300 mb-2 font-medium' },
          date.toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' })
        ),
        React.createElement(
          'div',
          { className:'space-y-1' },
          payload.map((entry, i) => {
            const line = (data.lines || []).find(l => l.dataKey === entry.dataKey);
            return React.createElement(
              'div',
              { key:i, className:'tooltip-item flex items-center justify-between gap-3' },
              React.createElement(
                'div',
                { className:'tooltip-label-wrapper flex items-center gap-2' },
                React.createElement('div', { className:'tooltip-color-box w-3 h-3 rounded-sm', style:{ backgroundColor: entry.color } }),
                React.createElement('span', { className:'tooltip-label text-xs' }, line && line.label ? line.label : entry.dataKey)
              ),
              React.createElement(
                'span',
                { className:'tooltip-value text-xs font-semibold' },
                Number(entry.value).toLocaleString('en-US', { style:'currency', currency:'USD', minimumFractionDigits:0, maximumFractionDigits:0 })
              )
            );
          })
        )
      );
    }

    // Legend
    const legendRow = React.createElement(
      'div',
      { className:'mb-6 pb-4 border-b border-gray-200 dark:border-gray-700' },
      React.createElement(
        'div',
        { className:'flex flex-wrap gap-4' },
        safeLines.map((line, idx) => {
          const isHighlighted = hoveredLine === line.dataKey;
          const isHighlightedByFilter = localHoveredFilter && matchesLineFilter(line, localHoveredFilter);
          const shouldDim = (hoveredLine && !isHighlighted) || (localHoveredFilter && !isHighlightedByFilter);
          const bold = isHighlighted || isHighlightedByFilter;

          const labelSpan = React.createElement(
            'span',
            { className:\`text-sm text-gray-900 dark:text-gray-100 relative inline-block \` },
            React.createElement('span', { className:'font-bold invisible', 'aria-hidden':'true' }, line.label),
            React.createElement('span', { className:\`absolute inset-0 \${bold ? 'font-bold' : ''}\` }, line.label)
          );

          const label = line.linkUrl
            ? React.createElement(
                'a',
                { href: line.linkUrl, target:'_blank', rel:'noopener noreferrer', className:'hover:underline' },
                labelSpan
              )
            : labelSpan;

          return React.createElement(
            'div',
            {
              key: idx,
              className: \`flex items-center gap-2 transition-all duration-200 \${line.linkUrl ? 'cursor-pointer' : ''} \${shouldDim ? 'opacity-50' : 'opacity-100'}\`,
              onMouseEnter: () => setHoveredLine(line.dataKey),
              onMouseLeave: () => setHoveredLine(null)
            },
            React.createElement('div', { className:'w-4 h-4 flex-shrink-0', style:{ backgroundColor: line.color } }),
            label
          );
        })
      )
    );

    // Chart
    const xAxisProps = {
      dataKey: 'date',
      tickFormatter: formatXAxis,
      tick: { fontSize: 12, fill: '#6b7280' },
      stroke: '#9ca3af'
    };
    
    if (xAxisLabel) {
      xAxisProps.label = {
        value: xAxisLabel,
        position: 'insideBottom',
        offset: -10,
        style: {
          fontSize: 11,
          fill: '#6b7280',
          fontWeight: 600,
          textTransform: 'uppercase'
        }
      };
    }

    const yAxisProps = {
      tickFormatter: formatYAxis,
      tick: { fontSize: 12, fill: '#6b7280' },
      stroke: '#9ca3af',
      label: {
        value: yAxisLabel,
        angle: -90,
        position: 'insideLeft',
        style: {
          fontSize: 11,
          fill: '#6b7280',
          fontWeight: 600,
          textTransform: 'uppercase',
          textAnchor: 'middle'
        }
      }
    };

    const chart = React.createElement(
      Recharts.ResponsiveContainer,
      { width:'100%', height:600 },
      React.createElement(
        Recharts.LineChart,
        { data: chartData, margin:{ top:20, right:20, bottom:20, left:20 } },
        React.createElement(Recharts.CartesianGrid, { strokeDasharray:'3 3', stroke:'#e5e7eb' }),
        React.createElement(Recharts.XAxis, xAxisProps),
        React.createElement(Recharts.YAxis, yAxisProps),
        React.createElement(Recharts.Tooltip, { content: React.createElement(CustomTooltip) }),
        safeLines.map(line =>
          React.createElement(Recharts.Line, {
            key: line.dataKey,
            type: 'linear',
            dataKey: line.dataKey,
            name: line.label,
            stroke: line.color,
            strokeWidth: (hoveredLine === line.dataKey || hoveredDot === line.dataKey || (localHoveredFilter && matchesLineFilter(line, localHoveredFilter))) ? 3 : 2,
            strokeOpacity: ((hoveredLine && hoveredLine !== line.dataKey && hoveredDot !== line.dataKey) || (localHoveredFilter && !matchesLineFilter(line, localHoveredFilter))) ? 0.3 : 1,
            dot: false,
            activeDot: {
              r: 5,
              onMouseEnter: () => { setHoveredDot(line.dataKey); setHoveredLine(line.dataKey); },
              onMouseLeave: () => { setHoveredDot(null); setHoveredLine(null); }
            },
            onMouseEnter: () => setHoveredLine(line.dataKey),
            onMouseLeave: () => setHoveredLine(null),
            isAnimationActive: false
          })
        )
      )
    );

    // Filters (optional)
    const filterRow = showLocalFilters
      ? React.createElement(
          'div',
          { className:'mt-3 pt-3 border-t border-gray-200 dark:border-gray-700' },
          React.createElement(StandaloneFilterControls, {
            data: data,
            activeFilter: localActiveFilter,
            hoveredFilter: localHoveredFilter,
            onFilterClick: (fid) => setLocalActiveFilter(fid),
            onFilterHover: (fid) => setLocalHoveredFilter(fid)
          })
        )
      : null;

    return React.createElement(
      'div',
      { className:'w-full' },
      title && React.createElement('h2', { className:'text-3xl font-bold text-gray-900 dark:text-gray-100 mb-6' }, title),
      legendRow,
      chart,
      filterRow
    );
  }
`;

export const BAR_CHART_TEMPLATE = `
  // === Bar chart helpers ===
  function StandaloneBarFilterControls({ data, activeFilter, hoveredFilter, onFilterClick, onFilterHover }) {
    const POSITION_ORDER = ['mayor', 'city council'];
    const structure = { positions: {} };
    const items = Array.isArray(data) ? data : [];
    
    items.forEach(item => {
      const pos = item.position || 'Other';
      if (!structure.positions[pos]) structure.positions[pos] = { subs: new Set() };
      if (item.subregion_value) structure.positions[pos].subs.add(item.subregion_value);
    });
    
    const sortedPositions = Object.keys(structure.positions).sort((a,b) => {
      const ai = POSITION_ORDER.indexOf(a.toLowerCase());
      const bi = POSITION_ORDER.indexOf(b.toLowerCase());
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return a.localeCompare(b);
    });
    
    const opts = [{ id:'all', label:'All', type:'all' }];
    sortedPositions.forEach(p => {
      const pos = structure.positions[p];
      opts.push({ id:p, label:p, type:'position' });
      Array.from(pos.subs).sort().forEach(s => {
        opts.push({ id:\`\${p}:\${s}\`, label:s, type:'subregion', parentPosition:p });
      });
    });

    return React.createElement(
      'div',
      { className:'flex flex-wrap gap-2 items-baseline' },
      opts.map((f, idx) => {
        const isActive = activeFilter === f.id;
        const isHovered = hoveredFilter === f.id;
        const pieces = [];
        const showOpen = f.type === 'subregion' && idx > 0 && opts[idx-1].type === 'position';
        if (showOpen) pieces.push(React.createElement('span', { key:\`o-\${f.id}\`, className:'text-gray-400 dark:text-gray-600 mx-1' }, '('));
        pieces.push(
          React.createElement('button', {
            key:f.id,
            onClick: () => onFilterClick && onFilterClick(f.id),
            onMouseEnter: () => onFilterHover && onFilterHover(f.id),
            onMouseLeave: () => onFilterHover && onFilterHover(null),
            className:\`px-2 py-1 rounded transition-all duration-200 cursor-pointer \${isActive ? 'font-bold text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'} \${!isActive && !isHovered ? 'opacity-70' : 'opacity-100'}\`
          }, f.label)
        );
        const next = opts[idx+1];
        const sameParentNext = next && next.parentPosition === f.parentPosition;
        if (f.type === 'subregion' && next && sameParentNext) pieces.push(React.createElement('span', { key:\`c-\${f.id}\`, className:'text-gray-400 dark:text-gray-600' }, ','));
        if (f.type === 'subregion' && next && !sameParentNext) pieces.push(React.createElement('span', { key:\`cl-\${f.id}\`, className:'text-gray-400 dark:text-gray-600 mx-1' }, ')'));
        if (f.type === 'subregion' && !next) pieces.push(React.createElement('span', { key:\`clx-\${f.id}\`, className:'text-gray-400 dark:text-gray-600 mx-1' }, ')'));
        return React.createElement(React.Fragment, { key:\`frag-\${f.id}\` }, pieces);
      })
    );
  }

  function matchesBarFilter(item, filterId) {
    if (filterId === 'all') return true;
    if (item.position === filterId) return true;
    if (filterId && filterId.indexOf(':') !== -1) {
      const parts = filterId.split(':');
      return item.position === parts[0] && String(item.subregion_value) === String(parts[1]);
    }
    return false;
  }
  
  function formatDollars(val) {
    if (val >= 1000000) return '$' + (val/1000000).toFixed(1) + 'M';
    if (val >= 1000) return '$' + (val/1000).toFixed(0) + 'K';
    return '$' + val;
  }

  function SegmentedBarChart({ data, title, legendLabel, legendOrder, legendColorMap, segmentOrder, activeFilter, hideEndLabels, showLocalFilters, xAxisLabel }) {
    const { useState, useEffect } = React;
    const [hoveredSegment, setHoveredSegment] = useState(null);
    const [hoveredLabel, setHoveredLabel] = useState(null);
    const [hoveredLabelSource, setHoveredLabelSource] = useState(null);
    const [isNarrow, setIsNarrow] = useState(false);
    const [localHoveredFilter, setLocalHoveredFilter] = useState(null);
    const [localActiveFilter, setLocalActiveFilter] = useState(activeFilter || 'all');

    useEffect(() => {
      const check = () => setIsNarrow(window.innerWidth < 768);
      check();
      window.addEventListener('resize', check);
      return () => window.removeEventListener('resize', check);
    }, []);

    const getDisplayLabel = (label) => {
      if (!isNarrow) return label;
      const parts = String(label || '').split(' ');
      return parts[parts.length - 1];
    };

    const currentActiveFilter = showLocalFilters ? localActiveFilter : activeFilter;
    const filtered = (currentActiveFilter === 'all' ? data : data.filter(d => matchesBarFilter(d, currentActiveFilter)));

    // Build legend items from data
    const allSegs = {};
    data.forEach(item => (item.segments || []).forEach(seg => {
      const label = seg.label;
      if (!allSegs[label]) allSegs[label] = seg.color;
    }));
    
    const legendItems = Object.entries(allSegs).map(([label, color]) => ({
      label,
      color: (legendColorMap && legendColorMap[label]) || color
    })).sort((a,b) => {
      if (Array.isArray(legendOrder) && legendOrder.length) {
        const ai = legendOrder.indexOf(a.label);
        const bi = legendOrder.indexOf(b.label);
        const ap = (ai === -1) ? Number.MAX_SAFE_INTEGER : ai;
        const bp = (bi === -1) ? Number.MAX_SAFE_INTEGER : bi;
        if (ap !== bp) return ap - bp;
      }
      return a.label.localeCompare(b.label);
    });

    const withData = filtered.filter(it => (it.segments || []).reduce((s,seg)=>s+seg.value,0) > 0);
    const withoutData = filtered.filter(it => (it.segments || []).reduce((s,seg)=>s+seg.value,0) === 0);
    const groups = [];
    const seen = new Set();
    withData.forEach(it => {
      const key = it.subregion_value ? \`\${it.position} \${it.subregion_value}\` : it.position;
      if (!seen.has(key)) { seen.add(key); groups.push([key, []]); }
      groups.find(g => g[0] === key)[1].push(it);
    });
    const maxTotal = Math.max(...withData.map(it => (it.segments || []).reduce((s,seg)=>s+seg.value,0)), 1);

    const processedLabels = withData.map(item => getDisplayLabel(item.label));
    const longestNameLength = Math.max(...processedLabels.map(label => label.length), 0);
    const nameWidth = Math.max(longestNameLength * 8 + 8, 80);

    const legendBlock = legendItems.length > 1
      ? React.createElement(
          'div',
          { className:'mb-6 pb-4 border-b border-gray-200 dark:border-gray-700' },
          legendLabel ? React.createElement('div', { className:'text-xs font-semibold text-gray-600 dark:text-gray-400 mb-3 uppercase' }, legendLabel) : null,
          React.createElement(
            'div',
            { className:'flex flex-wrap gap-4' },
            legendItems.map((item, idx) => {
              const isHighlighted = hoveredLabel === item.label;
              return React.createElement(
                'div',
                {
                  key:idx,
                  className:\`flex items-center gap-2 transition-all duration-200 cursor-pointer \${hoveredLabel && !isHighlighted ? 'opacity-50' : 'opacity-100'}\`,
                  onMouseEnter: () => { setHoveredLabel(item.label); setHoveredLabelSource('legend'); },
                  onMouseLeave: () => { setHoveredLabel(null); setHoveredLabelSource(null); }
                },
                React.createElement('div', { className:'w-4 h-4 flex-shrink-0', style:{ backgroundColor:item.color } }),
                React.createElement(
                  'span',
                  { className:'text-sm text-gray-900 dark:text-gray-100 relative inline-block' },
                  React.createElement('span', { className:'font-bold invisible','aria-hidden':'true' }, item.label),
                  React.createElement('span', { className:\`absolute inset-0 \${isHighlighted ? 'font-bold' : ''}\` }, item.label)
                )
              );
            })
          )
        )
      : null;

    // X-axis label (if provided)
    const xAxisLabelBlock = xAxisLabel 
      ? React.createElement(
          'div',
          { className:'text-center mt-6 mb-2' },
          React.createElement('span', { 
            className:'text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider'
          }, xAxisLabel)
        )
      : null;

    return React.createElement(
      'div',
      { className:'w-full' },
      React.createElement(
        'div',
        { className:'flex justify-between items-start mb-6' },
        title ? React.createElement('h2', { className:'text-3xl font-bold text-gray-900 dark:text-gray-100' }, title) : null
      ),
      legendBlock,
      React.createElement(
        'div',
        { className:'space-y-8' },
        groups.map(([contestName, items]) => {
          const contestNoData = withoutData.filter(it => {
            const key = it.subregion_value ? \`\${it.position} \${it.subregion_value}\` : it.position;
            return key === contestName;
          });
          
          const allContestItems = [...items, ...contestNoData].sort((a, b) => {
            const getLastName = (fullName) => {
              const parts = String(fullName || '').trim().split(/\\s+/);
              return parts[parts.length - 1].toLowerCase();
            };
            return getLastName(a.label).localeCompare(getLastName(b.label));
          });
          
          return React.createElement(
            'div',
            { key:contestName },
            React.createElement(
              'div',
              { className:'flex items-center gap-4 mb-3' },
              React.createElement('span', { className:'inline-block text-xs font-bold text-gray-800 dark:text-gray-100 uppercase tracking-wide px-3 py-1.5 bg-gray-200 dark:bg-gray-700 rounded' }, contestName),
              React.createElement('div', { className:'flex-1 h-px bg-gray-300 dark:bg-gray-600' })
            ),
            React.createElement(
              'div',
              { className:'space-y-4' },
              allContestItems.map((item, idx) => {
                const total = (item.segments || []).reduce((s,seg)=>s+seg.value,0);
                const hasNoData = total === 0;
                const percent = (total / maxTotal) * 100;
                const isCand = hoveredSegment && hoveredSegment.candidateLabel === item.label;
                const isHovLab = hoveredLabel && (item.segments || []).some(s => s.label === hoveredLabel);
                const shouldDim = (hoveredSegment && !isCand) || (hoveredLabel && !isHovLab) || (localHoveredFilter && !matchesBarFilter(item, localHoveredFilter));
                const displayLabel = getDisplayLabel(item.label);
                const uniqueRowId = \`\${contestName}-\${String(item.label || '').replace(/\\s+/g, '-')}\`;
                
                if (hasNoData) {
                  return React.createElement(
                    'div',
                    { key:idx, className:\`flex items-center gap-4 transition-opacity duration-200 \${shouldDim ? 'opacity-40' : 'opacity-100'}\` },
                    item.imageUrl ? React.createElement('img', { 
                      src:item.imageUrl, 
                      className:'w-10 h-10 object-cover rounded flex-shrink-0',
                      alt:item.label
                    }) : null,
                    React.createElement(
                      'div',
                      { className:'flex items-center flex-shrink-0', style:{ width:\`\${nameWidth}px\` } },
                      item.linkUrl
                        ? React.createElement('a', { 
                            href:item.linkUrl, 
                            target:'_blank', 
                            rel:'noopener noreferrer', 
                            className:'text-sm font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap overflow-hidden text-ellipsis cursor-pointer hover:underline'
                          }, displayLabel)
                        : React.createElement('div', { className:'text-sm font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap overflow-hidden text-ellipsis' }, displayLabel)
                    ),
                    React.createElement('div', { className:'text-xs text-gray-500 dark:text-gray-400 w-3 flex-shrink-0 text-right' }, item.leftLabel || ''),
                    React.createElement(
                      'div',
                      { className:'flex-1 min-w-0 relative flex items-center' },
                      React.createElement(
                        'div',
                        { className:'flex h-10 w-full items-center' },
                        React.createElement('div', { className:'text-xs italic text-gray-400 dark:text-gray-500' }, 'No financial data submitted')
                      )
                    ),
                    React.createElement('div', { className:'w-20 flex-shrink-0' })
                  );
                }
                
                return React.createElement(
                  'div',
                  { key:idx, className:\`flex items-center gap-4 transition-opacity duration-200 \${shouldDim ? 'opacity-40' : 'opacity-100'}\` },
                  item.imageUrl ? React.createElement('img', { 
                    src:item.imageUrl, 
                    className:'w-10 h-10 object-cover rounded flex-shrink-0',
                    alt:item.label
                  }) : null,
                  React.createElement(
                    'div',
                    { className:'flex items-center flex-shrink-0', style:{ width:\`\${nameWidth}px\` } },
                    item.linkUrl
                      ? React.createElement('a', { 
                          href:item.linkUrl, 
                          target:'_blank', 
                          rel:'noopener noreferrer', 
                          className:'text-sm font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap overflow-hidden text-ellipsis cursor-pointer hover:underline'
                        }, displayLabel)
                      : React.createElement('div', { className:'text-sm font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap overflow-hidden text-ellipsis' }, displayLabel)
                  ),
                  React.createElement('div', { className:'text-xs text-gray-500 dark:text-gray-400 w-3 flex-shrink-0 text-right' }, item.leftLabel || ''),
                  React.createElement(
                    'div',
                    { className:'flex-1 min-w-0 relative flex items-center' },
                                      React.createElement(
                        'div',
                        { className:'flex h-10 w-full' },
                        (Array.isArray(segmentOrder) && segmentOrder.length
                          ? [...(item.segments || [])].sort((a, b) => {
                              const ai = segmentOrder.indexOf(a.label);
                              const bi = segmentOrder.indexOf(b.label);
                              const ap = (ai === -1) ? Number.MAX_SAFE_INTEGER : ai;
                              const bp = (bi === -1) ? Number.MAX_SAFE_INTEGER : bi;
                              return ap - bp;
                            })
                          : (item.segments || [])
                        ).map((seg, sIdx) => {
                          const segPercent = total > 0 ? (seg.value / total) * 100 : 0;
                          const barWidthPercent = (total / maxTotal) * 100;
                          const segmentWidth = (segPercent / 100) * barWidthPercent;
                          const isSegHov = hoveredSegment && hoveredSegment.candidateLabel === item.label && hoveredSegment.segmentLabel === seg.label;
                          const isLabelHov = hoveredLabel && hoveredLabel === seg.label && (!hoveredSegment || hoveredLabelSource !== 'segment');
                          const highlight = isSegHov || isLabelHov;
                          return React.createElement('div', {
                            key:sIdx,
                            className:'transition-all duration-200 cursor-pointer relative',
                            style:{ 
                              width:\`\${segmentWidth}%\`, 
                              backgroundColor: seg.color,
                              opacity: hoveredLabel && hoveredLabel !== seg.label ? 0.3 : 1,
                              filter: highlight ? 'brightness(1.1)' : 'none',
                              transform: highlight ? 'scaleY(1.05)' : 'scaleY(1)'
                            },
                            onMouseEnter: () => {
                              setHoveredSegment({ candidateLabel:item.label, segmentLabel:seg.label });
                              setHoveredLabel(seg.label);
                              setHoveredLabelSource('segment');
                            },
                            onMouseLeave: () => {
                              setHoveredSegment(null);
                              setHoveredLabel(null);
                              setHoveredLabelSource(null);
                            }
                          },
                          isSegHov && hoveredLabelSource === 'segment' && seg.tooltipText ? React.createElement(
                            'div',
                            { 
                              className:'absolute -top-12 left-1/2 transform -translate-x-1/2 bg-gray-900 dark:bg-gray-700 text-white px-3 py-2 rounded shadow-lg z-20 whitespace-nowrap'
                            },
                            React.createElement('p', { className:'text-xs font-semibold' }, seg.label),
                            React.createElement('p', { className:'text-xs text-gray-300' }, seg.tooltipText)
                          ) : null);
                        })
                      ),
                    !hideEndLabels ? React.createElement(
                      'div',
                      {
                        className:'absolute text-sm font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap ml-2',
                        style:{ left: \`\${(total / maxTotal) * 100}%\` }
                      },
                      item.formattedTotal || formatDollars(total)
                    ) : null
                  ),
                  React.createElement('div', { className:'w-20 flex-shrink-0' })
                );
              })
            )
          );
        })
      ),
      xAxisLabelBlock,
      showLocalFilters ? React.createElement(
        'div',
        { className:'mt-3 pt-3 border-t border-gray-200 dark:border-gray-700' },
        React.createElement(StandaloneBarFilterControls, {
          data,
          activeFilter: localActiveFilter,
          hoveredFilter: localHoveredFilter,
          onFilterClick: (fid) => setLocalActiveFilter(fid),
          onFilterHover: (fid) => setLocalHoveredFilter(fid)
        })
      ) : null
    );
  }
`;